import { useEffect, useState } from 'react';
import { mockCompany } from '@/mocks/masters';
import { fetchCompanyDetails, type PrintInvoiceData } from '@/utils/printDocument';

interface PrintSalesInvoiceProps {
  data: PrintInvoiceData;
  onClose: () => void;
  onPrint: () => void;
}

interface Company {
  id: string;
  name: string;
  address: string;
  gstin: string;
  pan: string;
  phone: string;
  email: string;
  logo_url?: string | null;
  bank_name?: string;
  bank_account?: string;
  bank_ifsc?: string;
}

function formatINR(n: number) {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
}

function numberToWords(n: number): string {
  const ones = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const tens = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety',
  ];

  if (n === 0) return 'Zero';
  const convert = (num: number): string => {
    if (num < 20) return ones[num];
    if (num < 100)
      return (
        tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '')
      );
    if (num < 1000)
      return (
        ones[Math.floor(num / 100)] +
        ' Hundred' +
        (num % 100 ? ' ' + convert(num % 100) : '')
      );
    if (num < 100000)
      return (
        convert(Math.floor(num / 1000)) +
        ' Thousand' +
        (num % 1000 ? ' ' + convert(num % 1000) : '')
      );
    if (num < 10000000)
      return (
        convert(Math.floor(num / 100000)) +
        ' Lakh' +
        (num % 100000 ? ' ' + convert(num % 100000) : '')
      );
    return (
      convert(Math.floor(num / 10000000)) +
      ' Crore' +
      (num % 10000000 ? ' ' + convert(num % 10000000) : '')
    );
  };

  const rupees = Math.floor(n);
  const paise = Math.round((n - rupees) * 100);

  if (paise > 0) {
    return `${convert(rupees)} Rupees and ${convert(paise)} Paise Only`;
  }
  return `${convert(rupees)} Rupees Only`;
}

export default function PrintSalesInvoice({
  data,
  onClose,
  onPrint,
}: PrintSalesInvoiceProps) {
  // ✅ State with mock as default
  const [company, setCompany] = useState<Company>(mockCompany);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ✅ Fetch company on mount (removed since we have it in the invoice object directly)
  // Instead, we derive it from the invoice data directly.
  useEffect(() => {
    setCompany({
      id: "company-id", // optional or derived
      name: data.companyName || mockCompany.name,
      address: data.companyAddress || mockCompany.address,
      gstin: data.companyGstin || mockCompany.gstin,
      pan: mockCompany.pan,
      phone: data.companyPhone || mockCompany.phone,
      email: mockCompany.email,
      website: mockCompany.website,
      bank_name: '',
      bank_account: '',
      bank_ifsc: '',
      logo_url: null,
    });
    // Bank details: the primary bank account from Settings, mirrored on the company profile
    fetchCompanyDetails()
      .then((co) => {
        if (co) {
          setCompany((prev) => ({
            ...prev,
            bank_name: co.bank_name || '',
            bank_account: co.bank_account || '',
            bank_ifsc: co.bank_ifsc || '',
          }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [data]);

  const c = company;

  // ✅ Logo handling
  const companyLogo = c.logo_url || null;
  let logoUrl = null;
  if (companyLogo) {
    if (companyLogo.startsWith('data:image')) {
      logoUrl = companyLogo;
    } else {
      logoUrl = `${import.meta.env.VITE_API_URL}${companyLogo}`;
    }
  }

  const bankName = c.bank_name || '';
  const bankAccount = c.bank_account || '';
  const bankIfsc = c.bank_ifsc || '';
  const amountInWords = numberToWords(data.grandTotal);

  // ✅ Agar loading hai toh loading show karo
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 flex items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-[#4f46e5] text-2xl" />
          <span className="text-sm text-slate-600">
            Loading company details...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8"
      onClick={onClose}
    >
      <div
        className="bg-white w-[210mm] min-h-[297mm] shadow-2xl rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-3 bg-[#1e293b] text-white">
          <div className="flex items-center gap-2">
            <i className="ri-file-text-line text-indigo-300" />
            <span className="text-sm font-medium">
              Tax Invoice Preview — {data.invoiceNo}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onPrint}
              className="flex items-center gap-1.5 h-7 px-3 rounded text-xs font-medium bg-[#4f46e5] hover:bg-indigo-600 text-white cursor-pointer whitespace-nowrap"
            >
              <i className="ri-printer-line" /> Print (F10)
            </button>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded hover:bg-white/10 cursor-pointer text-slate-300"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        </div>

        {/* Invoice content */}
        <div className="p-8 text-[11px] font-sans">
          {/* ─── HEADER ─── */}
          <div className="flex justify-between items-start mb-5 pb-4 border-b-2 border-[#e2e8f0]">
            <div className="flex items-start gap-4">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={c.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-[#4f46e5] bg-white shrink-0"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center text-2xl font-bold text-[#4f46e5] border-2 border-[#4f46e5] shrink-0">
                  {c.name?.charAt(0)?.toUpperCase() || 'I'}
                </div>
              )}
              <div>
                <h1 className="text-lg font-bold text-[#1e293b]">
                  {c.name || 'Company Name'}
                </h1>
                <p className="text-[#64748b] mt-1 leading-relaxed text-xs">
                  {c.address || ''}
                </p>
                <p className="mt-1">
                  <strong>GSTIN:</strong> {c.gstin || ''} 
                </p>
                <p><strong>PAN:</strong> {c.pan || ''}</p>
                <p className="text-xs">
                  <strong>Ph:</strong> {c.phone || ''} &nbsp;|&nbsp; <strong>Email:</strong> {c.email || ''}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl font-extrabold text-[#4f46e5] tracking-wide">
                TAX INVOICE
              </p>
              <div className="mt-2 border border-[#e2e8f0] rounded-lg p-3 text-left inline-block">
                <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide">
                  Invoice No
                </p>
                <p className="font-bold text-base text-[#1e293b]">
                  {data.invoiceNo}
                </p>
                <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide mt-2">
                  Date
                </p>
                <p>{data.date}</p>
                <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide mt-2">
                  Payment
                </p>
                <p>{data.paymentMode}</p>
              </div>
            </div>
          </div>

          {/* ─── DISPATCHED FROM / BILL TO / SHIP TO (ONE ROW) ─── */}
          <div className={`grid ${data.warehouseName ? 'grid-cols-3' : 'grid-cols-2'} gap-4 mb-5 p-3 bg-[#f8fafc] rounded-lg`}>
            {data.warehouseName && (
              <div>
                <p className="text-[10px] text-[#4f46e5] uppercase tracking-wide font-bold mb-1">
                  Dispatched From (Warehouse)
                </p>
                <p className="font-bold text-sm text-[#1e293b]">
                  {data.warehouseName}
                </p>
                {data.warehouseGstin && (
                  <p className="text-[#4f46e5] font-mono text-[10px] mt-0.5">
                    GSTIN: {data.warehouseGstin}
                  </p>
                )}
                {data.warehouseAddress && (
                  <p className="text-[#64748b] mt-0.5 leading-relaxed text-xs">
                    {data.warehouseAddress}
                  </p>
                )}
              </div>
            )}
            <div>
              <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide font-semibold mb-1">
                Bill To
              </p>
              <p className="font-bold text-sm text-[#1e293b]">
                {data.customerName}
              </p>
              {data.customerGstin && (
                <p className="text-[#64748b] mt-0.5">
                  GSTIN: {data.customerGstin}
                </p>
              )}
              <p className="text-[#64748b] mt-0.5 leading-relaxed text-xs">
                {data.billingAddress}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide font-semibold mb-1">
                Ship To
              </p>
              <p className="font-bold text-sm text-[#1e293b]">
                {data.customerName}
              </p>
              <p className="text-[#64748b] mt-0.5 leading-relaxed text-xs">
                {data.shippingAddress || data.billingAddress}
              </p>
            </div>
          </div>

          {/* ─── AMOUNT IN WORDS + BANK DETAILS ─── */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div className="p-3 bg-[#f8fafc] rounded-lg border-l-4 border-[#4f46e5]">
              <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide mb-1">
                Amount in Words
              </p>
              <p className="font-semibold text-sm text-[#1e293b]">
                {amountInWords}
              </p>
            </div>
            {bankAccount ? (
              <div className="p-3 bg-[#f8fafc] rounded-lg text-[10px]">
                <p className="text-[#94a3b8] uppercase tracking-wide mb-1 font-semibold">
                  Bank Details
                </p>
                <p>
                  <strong>Bank:</strong> {bankName} &nbsp;|&nbsp;{' '}
                  <strong>A/C:</strong> {bankAccount}
                </p>
                {bankIfsc && (
                  <p>
                    <strong>IFSC:</strong> {bankIfsc}
                  </p>
                )}
              </div>
            ) : (
              <div />
            )}
          </div>

          {/* ─── ITEMS TABLE ─── */}
          <table className="w-full border-collapse border border-[#e2e8f0] text-[10px] mb-5">
            <thead>
              <tr className="bg-[#f8fafc]">
                {[
                  'Sr',
                  'Description',
                  'HSN',
                  'Qty',
                  'Unit',
                  'Rate',
                  'Disc%',
                  'Taxable',
                  'GST%',
                  'Tax Amt',
                  'Total',
                ].map((h, i) => (
                  <th
                    key={i}
                    className={`border border-[#e2e8f0] px-2 py-1.5 text-left font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap ${i >= 3 ? 'text-right' : ''}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map((item, i) => (
                <tr key={i} className={i % 2 === 1 ? 'bg-[#fafafa]' : ''}>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-center">
                    {item.sr}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 font-medium">
                    <div>{item.name}</div>
                    {(item.batchNumber || (item as any).batch_number) && (
                      <div className="text-[9px] text-[#065f46] font-mono font-normal">
                        Batch: {item.batchNumber || (item as any).batch_number}
                        {item.expiryDate ? ` · Exp: ${String(item.expiryDate).slice(0, 10)}` : ''}
                        {item.mfgDate ? ` · Mfg: ${String(item.mfgDate).slice(0, 10)}` : ''}
                      </div>
                    )}
                    {(() => {
                      const raw = (Array.isArray((item as any).serials) && (item as any).serials.length > 0)
                        ? (item as any).serials
                        : (Array.isArray((item as any).serialNumbers) && (item as any).serialNumbers.length > 0)
                        ? (item as any).serialNumbers
                        : (typeof (item as any).serialNumbers === 'string' && (item as any).serialNumbers.trim())
                        ? (item as any).serialNumbers
                        : (Array.isArray((item as any).selectedSerials) && (item as any).selectedSerials.length > 0)
                        ? (item as any).selectedSerials
                        : (item as any).serials || [];

                      let rawList: any[] = [];
                      if (Array.isArray(raw)) {
                        rawList = raw;
                      } else if (typeof raw === 'string') {
                        rawList = raw.split(/[,\n]+/).map((s: string) => s.trim()).filter(Boolean);
                      } else if (raw) {
                        rawList = [raw];
                      }

                      const list: string[] = rawList
                        .map((s: any) => typeof s === 'string' ? s : (s.serialNumber || s.serial_number || s.serialNo || s.id || ''))
                        .filter(Boolean);
                      if (list.length === 0) return null;
                      return (
                        <div className="text-[9px] text-[#4338ca] font-mono font-normal mt-0.5">
                          S/N: {list.join(', ')}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5">
                    {item.hsn}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-right">
                    {item.qty}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5">
                    {item.unit}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-right">
                    {formatINR(item.rate)}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-right">
                    {item.discount > 0 ? `${item.discount}%` : '—'}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-right">
                    {formatINR(item.taxable)}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-right">
                    {item.taxRate}%
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-right">
                    {formatINR(item.taxAmt)}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-right font-bold">
                    {formatINR(item.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ─── PAYMENT DETAILS + TOTALS ─── */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            {data.showPaymentTable !== false && (
              <div className="border border-[#e2e8f0] rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-[#e2e8f0]">
                  <p className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wide">
                    Payment Details
                  </p>
                </div>
                {[
                  { label: 'Payment Mode', value: data.paymentMode || '—' },
                  { label: 'Payment Status', value: data.paymentStatus || '—' },
                  {
                    label: 'Invoice Amount',
                    value: formatINR(data.grandTotal ?? 0),
                  },
                  {
                    label: 'Amount Paid',
                    value: formatINR(data.paidAmount ?? 0),
                  },
                  {
                    label: 'Balance Due',
                    value: formatINR(data.balanceDue ?? 0),
                  },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex justify-between px-3 py-1.5 border-b border-[#f1f5f9] text-[10px] last:border-0"
                  >
                    <span className="text-[#64748b]">{row.label}</span>
                    <span className="font-semibold text-[#1e293b]">
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="border border-[#e2e8f0] rounded-lg overflow-hidden">
              {[
                { label: 'Subtotal', value: formatINR(data.subtotal) },
                {
                  label: 'Discount',
                  value:
                    data.totalDiscount > 0
                      ? `- ${formatINR(data.totalDiscount)}`
                      : '—',
                },
                {
                  label: 'Taxable Amount',
                  value: formatINR(data.taxableAmount),
                },
                ...(data.isSameState
                  ? [
                      { label: 'CGST', value: formatINR(data.cgst) },
                      { label: 'SGST', value: formatINR(data.sgst) },
                    ]
                  : [{ label: 'IGST', value: formatINR(data.igst) }]),
                {
                  label: 'Round Off',
                  value:
                    data.roundOff !== 0
                      ? formatINR(Math.abs(data.roundOff))
                      : '—',
                },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex justify-between px-3 py-1.5 border-b border-[#f1f5f9] text-[10px] last:border-0"
                >
                  <span className="text-[#64748b]">{row.label}</span>
                  <span className="font-semibold text-[#1e293b]">
                    {row.value}
                  </span>
                </div>
              ))}
              <div className="flex justify-between px-3 py-2 bg-[#4f46e5] text-white font-bold text-sm">
                <span>Grand Total</span>
                <span>{formatINR(data.grandTotal)}</span>
              </div>
            </div>
          </div>

          {/* ─── TDS NOTE ─── */}
          {data.tdsApplicable && (
            <div
              style={{
                marginTop: 16,
                marginBottom: 20,
                padding: 12,
                border: '1px solid #E5E7EB',
                borderLeft: '4px solid #2563EB',
                backgroundColor: '#F0F4FF',
                fontSize: 11,
              }}
            >
              <strong>TDS NOTE:</strong>
              <br />
              This invoice is subject to TDS deduction under Section {data.tdsSection} of the
              Income Tax Act, 1961 @ {data.tdsRate}% on taxable value of {formatINR(data.taxableAmount)}.
              <br />
              TDS Amount: {formatINR(data.tdsAmount ?? 0)}
              <br />
              Net payable after TDS: {formatINR(data.expectedReceipt ?? data.grandTotal)}
              <br />
              <em>Please provide Form 16A for TDS deducted.</em>
            </div>
          )}

          {/* ─── TCS NOTE ─── */}
          {data.tcsApplicable && (
            <div
              style={{
                marginTop: 16,
                marginBottom: 20,
                padding: 12,
                border: '1px solid #E5E7EB',
                borderLeft: '4px solid #9333EA',
                backgroundColor: '#FAF5FF',
                fontSize: 11,
              }}
            >
              <strong>TCS NOTE:</strong>
              <br />
              TCS collected under Section 206C(1H) of the Income Tax Act, 1961 @ {data.tcsRate}%.
              <br />
              TCS Amount: {formatINR(data.tcsAmount ?? 0)} (already included in Grand Total above)
              <br />
              <em>TCS credit of {formatINR(data.tcsAmount ?? 0)} is claimable in your Income Tax Return.</em>
            </div>
          )}

          {/* ─── TAX SUMMARY ─── */}
          {/* <div className="mb-5 border border-[#e2e8f0] rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-[#e2e8f0]">
              <p className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wide">
                Tax Summary (Rate-wise Breakup)
              </p>
            </div>
            <table className="w-full text-[10px]">
              <thead>
                <tr className="bg-[#f8fafc]">
                  <th className="px-3 py-1.5 text-left font-semibold text-[#64748b] border-b border-[#e2e8f0]">
                    GST Rate
                  </th>
                  <th className="px-3 py-1.5 text-right font-semibold text-[#64748b] border-b border-[#e2e8f0]">
                    Taxable Amount
                  </th>
                  <th className="px-3 py-1.5 text-right font-semibold text-[#64748b] border-b border-[#e2e8f0]">
                    CGST
                  </th>
                  <th className="px-3 py-1.5 text-right font-semibold text-[#64748b] border-b border-[#e2e8f0]">
                    SGST
                  </th>
                  <th className="px-3 py-1.5 text-right font-semibold text-[#64748b] border-b border-[#e2e8f0]">
                    Total Tax
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.isSameState ? (
                  <tr>
                    <td className="px-3 py-1.5 border-b border-[#f1f5f9]">
                      18%
                    </td>
                    <td className="px-3 py-1.5 text-right border-b border-[#f1f5f9]">
                      {formatINR(data.taxableAmount)}
                    </td>
                    <td className="px-3 py-1.5 text-right border-b border-[#f1f5f9]">
                      {formatINR(data.cgst)}
                    </td>
                    <td className="px-3 py-1.5 text-right border-b border-[#f1f5f9]">
                      {formatINR(data.sgst)}
                    </td>
                    <td className="px-3 py-1.5 text-right border-b border-[#f1f5f9] font-semibold">
                      {formatINR(data.cgst + data.sgst)}
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td className="px-3 py-1.5 border-b border-[#f1f5f9]">
                      18%
                    </td>
                    <td className="px-3 py-1.5 text-right border-b border-[#f1f5f9]">
                      {formatINR(data.taxableAmount)}
                    </td>
                    <td
                      className="px-3 py-1.5 text-right border-b border-[#f1f5f9]"
                      colSpan={2}
                    >
                      —
                    </td>
                    <td className="px-3 py-1.5 text-right border-b border-[#f1f5f9] font-semibold">
                      {formatINR(data.igst)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div> */}

          {/* ─── SIGNATURE ─── */}
          <div className="flex justify-end mt-8 pt-4 border-t border-[#e2e8f0]">
            <div className="text-center text-[10px] text-[#64748b] w-44">
              <p className="font-semibold text-[#1e293b]">
                {c.name || 'Company Name'}
              </p>
              <p className="mt-4 border-t border-[#e2e8f0] pt-1">
                Authorised Signatory
              </p>
            </div>
          </div>

          <p className="text-center text-[9px] text-[#94a3b8] italic mt-4">
            This is a computer generated invoice. No signature required if
            digitally signed.
          </p>
        </div>
      </div>
    </div>
  );
}
